import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useApolloClient, useLazyQuery } from "@apollo/client";
import { appConfig } from "../../../configuration/AppConfig";
import { GET_MENU_ITEMS } from "../../../queries/menuQrys";

import { GET_SAMPLE_SCHEDULE_REPORT } from "../../../queries/publicQrys/WaterSystemReportQrys";
import UtahHeader from "./UtahHeader";
import { useWaterSystemSearchModal } from "../WaterSystemSearchModalContext";
import { Modal, ModalTitle, ModalContent, ModalFooter, Button } from "@utahdts/utah-design-system";
import { buildHeaderMenuItems } from "./headerMenuUtils";
import { buildRevokeUrl, clearAuthCookie, clearAuthState } from "../auth/logoutUtils";
import "./Header.css";

const ACCESS_TOKEN_KEY = "token";

function Header({
  setRawMenuItemsArray,
  rawMenuItemsArray,
  selectedWaterSystem,
  providerUser,
  redirectPath,
  setRedirectPath,
}) {
  const client = useApolloClient();
  const history = useHistory();
  const location = useLocation();
  const { setShowSystemSelectModal } = useWaterSystemSearchModal();
  const [errorModalTitle] = useState("");
  const [errorModalContent] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [downloadRequested, setDownloadRequested] = useState(false);

  const [getSampleScheduleReport, { data: sampleScheduleData }] = useLazyQuery(GET_SAMPLE_SCHEDULE_REPORT);

  const setAuthCookie = useCallback((name, value) => {
    const expires = new Date(Date.now() + 10 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  const triggerCsvDownload = useCallback(() => {
    setDownloadRequested(true);
    getSampleScheduleReport();
  }, [getSampleScheduleReport]);

  const logout = useCallback(() => {
    clearAuthCookie();
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    clearAuthState();

    const revokeUrl = buildRevokeUrl(appConfig.revoke, appConfig.authParams, token);

    window.location.assign(revokeUrl);
  }, []);

  const handleSignIn = useCallback(() => {
    setAuthCookie("v2Authenticated", "true");
    window.location.assign(`${appConfig.authEndpoint}${appConfig.authParams}`);
  }, [setAuthCookie]);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const { data: menuData } = await client.query({
          query: GET_MENU_ITEMS,
          variables: { pwsId: selectedWaterSystem?.pwsId || null },
          fetchPolicy: "network-only",
        });

        if (menuData?.getMenuItems) {
          setRawMenuItemsArray(menuData.getMenuItems.sections || []);
        } else {
          setRawMenuItemsArray([]);
        }
      } catch (menuFetchErr) {
        console.error("Failed to fetch menu items:", menuFetchErr);
        setRawMenuItemsArray([]);
      }
    };

    fetchMenu();
  }, [client, selectedWaterSystem?.pwsId, setRawMenuItemsArray]);

  useEffect(() => {
    if (downloadRequested && sampleScheduleData?.getSampleScheduleReportCsv) {
      const reportObj = sampleScheduleData.getSampleScheduleReportCsv;
      const csvFileStream = reportObj.stream.replace(/\r?\n/g, "\r\n");
      const csvFileName = reportObj.filename;
      const blob = new Blob([csvFileStream], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = csvFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setDownloadRequested(false);
    }
  }, [downloadRequested, sampleScheduleData]);

  const processedMenuItems = useMemo(() => {
    if (!Array.isArray(rawMenuItemsArray)) return [];

    return buildHeaderMenuItems({
      items: rawMenuItemsArray,
      pathname: location.pathname,
      search: location.search,
      selectedWaterSystem,
      history,
      setShowSystemSelectModal,
      triggerCsvDownload,

    });
  }, [
    rawMenuItemsArray,
    location.pathname,
    location.search,
    selectedWaterSystem,
    history,
    setShowSystemSelectModal,
    triggerCsvDownload,

  ]);

  useEffect(() => {
    if (redirectPath) {
      history.push(redirectPath);
      setRedirectPath(null);
    }
  }, [redirectPath, history, setRedirectPath]);

  return (
    <>
      {showErrorModal && (
        <div className="utah-design-system">
          <Modal
            className="modal--medium"
            onClose={handleCloseModal}
            onEscape={handleCloseModal}
          >
            <ModalTitle>{errorModalTitle}</ModalTitle>
            <ModalContent id="WaterSystemRedirect-ErrorModal-Content">
              <p>{errorModalContent}</p>
            </ModalContent>
            <ModalFooter id="WaterSystemRedirect-ErrorModal-Footer">
              <Button onClick={handleCloseModal}>Close</Button>
            </ModalFooter>
          </Modal>
        </div>
      )}
      <UtahHeader
        providerUser={providerUser}
        menuItemsArray={processedMenuItems}
        onSignIn={handleSignIn}
        onSignOut={logout}
      />
    </>
  );
}

export default Header;